/** @jsxImportSource theme-ui */
import { useState } from "react";
import { useSelector } from "react-redux";
import { Box, Button as ThemeUiButton, Text } from "theme-ui";
import { motion, AnimatePresence } from "framer-motion";
import OutsideClickHandler from "react-outside-click-handler";
import { get } from "lodash/fp";

import { useTranslation } from "hooks";
import { selectHasAllRequiredValues } from "store/app";
import { Button } from "components";
import { PlusIcon } from "components/icons";
import * as styles from "./ActionsBarMobile.styles";

export default function ActionsBarMobile({
  isOutOfStock,
  dispatchAddToOrder,
  displayReviewModal,
  toggleMobileMenu,
  selectedImageViewAngle,
  imageViewAnglesObj = {},
  setSelectedImageAngle,
}) {
  const { t } = useTranslation();
  const [isChangeViewMenuOpen, setIsChangeViewMenuOpen] = useState(false);
  const toggleChangeViewMenu = () => {
    setIsChangeViewMenuOpen(!isChangeViewMenuOpen);
  };

  const hasAllRequiredValues = useSelector(selectHasAllRequiredValues);

  const skipReviewModal = useSelector(
    get(["app", "formData", "skipReviewModal"])
  );

  const transition = { duration: 0.3 };

  return (
    <Box sx={styles.actionsBar}>
      <ThemeUiButton
        variant="unstyled"
        sx={styles.imagesButton({
          backgroundUrl: imageViewAnglesObj[selectedImageViewAngle]?.url,
        })}
        onClick={toggleChangeViewMenu}
      />

      <Button
        iconBefore={<PlusIcon />}
        size="large"
        onClick={skipReviewModal ? dispatchAddToOrder : displayReviewModal}
        disabled={!hasAllRequiredValues || isOutOfStock}
        sx={styles.addToOrderButton}
      >
        {isOutOfStock ? t("Unavailable") : t("Add To Order")}
      </Button>

      <ThemeUiButton
        variant="unstyled"
        sx={styles.menuButton}
        onClick={toggleMobileMenu}
        aria-label="open menu"
      >
        <svg
          width="17"
          height="16"
          viewBox="0 0 17 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M0.5 3.5H16.5" stroke="white" strokeLinecap="round" />
          <path d="M0.5 13.5H16.5" stroke="white" strokeLinecap="round" />
          <path d="M0.5 8.5H16.5" stroke="white" strokeLinecap="round" />
        </svg>
      </ThemeUiButton>

      <AnimatePresence>
        {isChangeViewMenuOpen && (
          <OutsideClickHandler
            onOutsideClick={() => {
              setIsChangeViewMenuOpen(false);
            }}
          >
            <motion.div
              sx={styles.changeImageDrawer}
              initial={{ y: "100%", transition }}
              animate={{ y: "0%", transition }}
              exit={{ y: "100%", transition }}
            >
              <Text
                variant="copy3"
                sx={{ textTransform: "uppercase", marginBottom: "10px" }}
              >
                {t("Change View")}
              </Text>

              <Box sx={styles.hideScroll}>
                <Box sx={styles.horizontalScrollContainer}>
                  <Box sx={styles.horizontalScroll}>
                    <Box sx={styles.imagesContainer}>
                      {Object.keys(imageViewAnglesObj).map((imageViewAngle) => {
                        const { url, alt } = imageViewAnglesObj[imageViewAngle];
                        return (
                          <ThemeUiButton
                            key={imageViewAngle}
                            variant="unstyled"
                            sx={styles.image({
                              backgroundImageUrl: url,
                              isSelected:
                                selectedImageViewAngle === imageViewAngle,
                            })}
                            aria-label={`${alt} view`}
                            onClick={() => {
                              if (isChangeViewMenuOpen) {
                                setSelectedImageAngle(imageViewAngle);
                              }
                              toggleChangeViewMenu();
                            }}
                          />
                        );
                      })}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </motion.div>
          </OutsideClickHandler>
        )}
      </AnimatePresence>
    </Box>
  );
}
